import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Container from '@mui/material/Container'
import Snackbar from '@mui/material/Snackbar'

import featureFlagClient, { Event } from 'src/utilities/feature-flag-client'
import axios, { pluckData } from 'src/utilities/http-client'
import Loading from 'src/v3/components/loading'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from '@mui/material'
import { LogoutButton } from 'src/authentication/auth-button'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  checkoutClientSecretChanged,
  checkoutPriceChanged,
  checkoutPromoCodeChanged,
  checkoutPromoResponseChanged,
  checkoutPromoValidationStatusChanged,
  selectCheckoutClientSecret,
  selectCheckoutPrice,
  selectCheckoutPromoCode,
  selectCheckoutPromoResponse,
  selectCheckoutPromoValidationStatus,
} from 'src/v3/settings/settings.slice'
import { CorrectIcon } from 'src/v3/core/icons'
import { Elems } from './components/checkout-form'

type UserInfoResponse = {
  userID: string
  firstName: string
  lastName: string
}

type PromoResponse = {
  valid: boolean
  message?: string
  code?: string
  discount?: number
  discountType?: string
  originalAmount?: number
  discountedAmount?: number
  newSubscriptionDetails: {
    subscriptionId: string
    clientSecret?: string
  }
}

export default ({ isBoundtree }: { isBoundtree: boolean }) => {
  let dispatch = useAppDispatch()
  let price = useAppSelector(selectCheckoutPrice)
  let promoCode = useAppSelector(selectCheckoutPromoCode)
  let secret = useAppSelector(selectCheckoutClientSecret)
  let promoValidationStatus = useAppSelector(
    selectCheckoutPromoValidationStatus,
  )
  let promoResponse = useAppSelector(selectCheckoutPromoResponse)
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [subscriptionID, setSubscriptionID] = useState('')
  let [isLoading, setIsLoading] = useState(false)
  let [enableCheckoutCouponField, setEnableCheckoutCouponField] =
    useState<boolean>(false)

  useEffect(() => {
    let client = featureFlagClient({
      identifier: 'engineering@foamfrat.com',
      attributes: {
        roles: [],
        linkedServiceIDs: [],
      },
    })

    client.on(Event.FLAGS_LOADED, () => {
      let hasEnabledCheckoutCouponField = client.variation(
        'enableCheckoutCouponField',
        false,
      ) as boolean

      if (hasEnabledCheckoutCouponField) {
        setEnableCheckoutCouponField(true)
      }
    })
  }, [])

  useEffect(() => {
    setIsLoading(true)
    axios
      .get<UserInfoResponse>('/api/subscription/user', {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('studio_jwt')}`,
        },
      })
      .then(pluckData)
      .then((userInfo) =>
        axios
          .post(
            `${import.meta.env.VITE_SUBSCRIPTION_API_URL}/api/subscription/customer`,
            userInfo,
            {
              headers: {
                Authorization: `Bearer ${sessionStorage.getItem('studio_jwt')}`,
              },
            },
          )
          .then(pluckData)
          .then(({ customerID }) =>
            axios
              .post(
                `${import.meta.env.VITE_SUBSCRIPTION_API_URL}/api/subscription`,
                { customerID, product: isBoundtree ? 'boundTree' : 'studio' },
                {
                  headers: {
                    Authorization: `Bearer ${sessionStorage.getItem('studio_jwt')}`,
                  },
                },
              )
              .then(pluckData)
              .then(({ clientSecret, subscriptionId }) => {
                dispatch(checkoutClientSecretChanged(clientSecret))
                setSubscriptionID(subscriptionId)
              }),
          ),
      )
      .catch((err) =>
        setErrorMessage(
          err.message || 'Error Loading User Subscription Information',
        ),
      )
      .finally(() => setIsLoading(false))
  }, [])

  useEffect(() => {
    axios
      .post(
        `${import.meta.env.VITE_SUBSCRIPTION_API_URL}/api/subscription/price`,
        { product: isBoundtree ? 'boundTree' : 'studio' },
      )
      .then(pluckData)
      .then((data) => dispatch(checkoutPriceChanged(data)))
  }, [])

  return (
    <Container component="main">
      {!isLoading && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mt: 1,
            }}
          >
            {isBoundtree ? (
              <img src="/btlogo.png" alt="logo" width={360} height={120} />
            ) : (
              <img src="/logo.svg" alt="logo" width={120} height={120} />
            )}
            {isBoundtree ? (
              <Typography
                sx={{ flex: '1 1 300px' }}
                data-testid="subscribe-copy-boundtree"
              >
                Welcome to FOAMfrat Studio! We're glad you want to upgrade to
                full access. Please purchase a subscription to continue to
                Studio with full access. All payments are securely processed by
                Stripe, a PCI-compliant payment provider trusted by millions of
                businesses worldwide. Your personal and payment information is
                encrypted and never stored on our servers. Once you have
                completed the transaction, you will be directed back to Studio.
              </Typography>
            ) : (
              <Typography
                sx={{ flex: '1 1 300px' }}
                data-testid="subscribe-copy-normal"
              >
                Welcome to FOAMfrat Studio! It appears you do not have an active
                subscription. Please purchase a subscription to continue to
                Studio. All payments are securely processed by Stripe, a
                PCI-compliant payment provider trusted by millions of businesses
                worldwide. Your personal and payment information is encrypted
                and never stored on our servers. Once you have completed the
                transaction, you will be directed back to the home page to sign
                in again.
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <LogoutButton
              data-testid="switch-accounts-button"
              size="small"
              color="primary"
              text="Log in with a different account"
              variant="outlined"
              sx={{ marginLeft: '1rem' }}
            />
          </Box>
          <Divider sx={{ mt: 1, mb: 2 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 5,
            }}
          >
            <Box sx={{ mt: 5, px: 5, mb: 2 }}>
              <Typography component="h1" variant="h6" color="text.secondary">
                Subscribe to FOAMfrat Studio
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                  width: 'min-content',
                }}
              >
                <Typography sx={{ fontSize: 32, fontWeight: 'bold' }}>
                  ${price?.amount}
                </Typography>
                <Typography sx={{ fontSize: 12 }} color="text.secondary">
                  per year
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 14 }}>Subtotal</Typography>
                <Typography sx={{ fontSize: 14 }}>${price?.amount}</Typography>
              </Box>
              {enableCheckoutCouponField && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 1.5,
                  }}
                >
                  <TextField
                    label="Add Promo Code"
                    size="small"
                    value={promoCode}
                    onChange={(event) =>
                      dispatch(checkoutPromoCodeChanged(event.target.value))
                    }
                    fullWidth
                  />
                  {promoValidationStatus === 'validating' ? (
                    <CircularProgress size={40} />
                  ) : (
                    <Button
                      variant="contained"
                      onClick={() => {
                        dispatch(
                          checkoutPromoValidationStatusChanged('validating'),
                        )
                        axios
                          .post<PromoResponse>(
                            `${import.meta.env.VITE_SUBSCRIPTION_API_URL}/api/promo/validate`,
                            {
                              subscriptionID,
                              promoCode,
                              originalAmount: price?.amount,
                            },
                          )
                          .then(pluckData)
                          .then((data) => {
                            setSubscriptionID(
                              data.newSubscriptionDetails.subscriptionId,
                            )
                            if (data.newSubscriptionDetails.clientSecret) {
                              dispatch(
                                checkoutClientSecretChanged(
                                  data.newSubscriptionDetails.clientSecret,
                                ),
                              )
                            }
                            dispatch(checkoutPromoResponseChanged(data))
                            if (!data.valid) {
                              setErrorMessage(
                                data.message || 'Invalid Promo Code',
                              )
                              dispatch(
                                checkoutPromoValidationStatusChanged('invalid'),
                              )
                            }
                          })
                          .catch((err) => {
                            setErrorMessage(
                              err?.message || 'Error Applying Promo Code',
                            )
                            dispatch(
                              checkoutPromoValidationStatusChanged('error'),
                            )
                          })
                          .finally(() =>
                            dispatch(
                              checkoutPromoValidationStatusChanged('valid'),
                            ),
                          )
                      }}
                    >
                      Apply
                    </Button>
                  )}
                </Box>
              )}
              {promoResponse?.valid && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: 'success.main',
                    borderRadius: 3,
                    gap: 1,
                    mt: 1.5,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  <CorrectIcon color="success" />
                  <Box>
                    <Typography>{promoResponse.code} Applied</Typography>
                    {promoResponse?.originalAmount &&
                      promoResponse?.discountedAmount && (
                        <Typography variant="subtitle2" color="text.secondary">
                          -$
                          {(
                            promoResponse.originalAmount -
                            promoResponse.discountedAmount
                          ).toFixed(2)}
                          {promoResponse.discountType === 'percent' && (
                            <span>{` (${promoResponse.discount}% off)`}</span>
                          )}
                        </Typography>
                      )}
                  </Box>
                </Box>
              )}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 18 }}>Total</Typography>
                <Typography sx={{ fontSize: 18 }}>
                  $
                  {(promoResponse?.discountedAmount || price?.amount)?.toFixed(
                    2,
                  )}
                </Typography>
              </Box>
            </Box>
            <Box>
              {promoValidationStatus !== 'validating' ? (
                secret ? (
                  <Elems clientSecret={secret} />
                ) : (
                  <></>
                )
              ) : (
                <></>
              )}
            </Box>
          </Box>
        </>
      )}
      <Loading entities={[]} loading={isLoading} />
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading entities={[]} loading={isLoading} />
    </Container>
  )
}
